'use client';

import { useEffect, useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { usePathname } from 'next/navigation';
import { Home, LayoutGrid, Search, User, ShoppingCart, GitCompare, Package, MessageCircle } from 'lucide-react';
import { useChatUI } from '@/stores/chat-ui';
import { useCartStore } from '@/stores/cart';
import { useCompareStore } from '@/stores/compare';
import { useWishlistStore } from '@/stores/wishlist';
import { useAuthStore } from '@/stores/auth';

/**
 * Persistent bottom navigation strip — mobile only. Five thumb-reachable
 * destinations covering 80% of the customer's actions. Hidden on:
 *  - product pages (StickyBuyBar owns that slot)
 *  - checkout (no extra navigation distraction during payment)
 */
export function MobileBottomNav() {
  const pathname = usePathname();
  const itemCount = useCartStore((s) => s.getItemCount());
  const compareCount = useCompareStore((s) => s.items.length);
  const wishlistCount = useWishlistStore((s) => s.ids.size);
  const token = useAuthStore((s) => s.token);
  const oppnaErik = useChatUI((s) => s.open);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Logged-out customers go straight to the login page; logged-in to their account.
  // Before hydration we don't know auth state, so default to /konto (it shows login
  // at the top anyway) and correct to /login once mounted.
  const accountHref = mounted && !token ? '/login' : '/konto';

  /*
   * Göms bara där något annat äger samma yta, eller där navigering skadar:
   * produktsidan har StickyBuyBar, kassan ska inte locka bort någon mitt i en
   * betalning.
   *
   * Inloggningssidorna stod här tidigare, och det gjorde Konto-knappen till en
   * fälla: en utloggad kund som tryckte Konto skickades till /login, och navet
   * gömde sig som direkt följd av att hon tryckt på det. Hela undre menyn
   * försvann och sidan blev en återvändsgränd utan väg tillbaka till Hem,
   * Katalog, Sök eller Varukorgen. Ägaren rapporterade det 2026-09-24 som
   * "trycker på konto så försvinner hela menyn — extremt konstigt".
   */
  const hide =
    pathname?.startsWith('/produkt/') ||
    pathname?.startsWith('/kassa') ||
    pathname?.startsWith('/foretag/konto/kassa') ||
    pathname?.startsWith('/foretag/mekaniker');

  if (hide) return null;

  /*
   * Jämför låg tidigare I Sök-platsen och tog över den så snart något lagts i
   * jämförelsen. Följden var att sökningen — mobilens enda väg in i sökfältet
   * sedan headerns ruta togs bort — kunde försvinna ur navet på grund av något
   * kunden gjort på en produktsida. En primär ingång får inte bytas ut mot en
   * sekundär. De fem platserna är därför fasta, och Jämför ligger som en egen
   * knapp ovanför listen när den har innehåll.
   */
  const showCompare = mounted && compareCount > 0;

  /*
   * I företagsportalen pekar navet på portalens egna sidor. Förut ledde
   * Varukorg härifrån till PRIVATKUNDENS /varukorg och /kassa — samma varor,
   * men utan företagsrabatt och utan faktura. Uppmätt 2026-09-25 med
   * Dr Rekonds konto i mobilen.
   */
  const b2b = pathname?.startsWith('/foretag/konto');
  const items: Array<{ href: string; label: string; icon: typeof Home; match: (p: string) => boolean; badge: number; klick?: () => void }> = b2b ? [
    { href: '/foretag/konto', label: 'Översikt', icon: Home, match: (p: string) => p === '/foretag/konto', badge: 0 },
    { href: '/foretag/konto/produkter', label: 'Reservdelar', icon: Search, match: (p: string) => p.startsWith('/foretag/konto/produkter'), badge: 0 },
    // Erik i menyn i stället för som flytande bubbla — bubblan låg över
    // "Till kassan" och "Lägg i varukorg" (ägaren 2026-09-25).
    { href: '#erik', label: 'Fråga Erik', icon: MessageCircle, match: () => false, badge: 0, klick: oppnaErik },
    { href: '/foretag/konto/bestallningar', label: 'Ordrar', icon: Package, match: (p: string) => p.startsWith('/foretag/konto/bestallningar'), badge: 0 },
    { href: '/foretag/konto/varukorg', label: 'Varukorg', icon: ShoppingCart, match: (p: string) => p.startsWith('/foretag/konto/varukorg'), badge: mounted ? itemCount : 0 },
  ] : [
    { href: '/', label: 'Hem', icon: Home, match: (p: string) => p === '/', badge: 0 },
    { href: '/katalog', label: 'Katalog', icon: LayoutGrid, match: (p: string) => p.startsWith('/katalog'), badge: 0 },
    { href: '/sok', label: 'Sök', icon: Search, match: (p: string) => p.startsWith('/sok'), badge: 0 },
    { href: accountHref, label: 'Konto', icon: User, match: (p: string) => p.startsWith('/konto') || p.startsWith('/login'), badge: mounted ? wishlistCount : 0 },
    { href: '/varukorg', label: 'Varukorg', icon: ShoppingCart, match: (p: string) => p.startsWith('/varukorg'), badge: mounted ? itemCount : 0 },
  ];

  return (
    <>
      {/* Jämför ligger ovanför listen i stället för i den, så att sökningen får
          behålla sin plats. Syns bara när det finns något att jämföra. */}
      {showCompare && !pathname?.startsWith('/jamfor') && (
        <Link
          href="/jamfor"
          className="fixed bottom-[68px] left-1/2 -translate-x-1/2 z-30 sm:hidden flex items-center gap-2
                     rounded-full bg-brand px-4 py-2 text-white text-sm font-medium
                     shadow-lg active:scale-95 transition-transform"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <GitCompare className="h-4 w-4" />
          Jämför {compareCount}
        </Link>
      )}

    <nav
      aria-label="Navigation"
      className="fixed bottom-0 inset-x-0 z-30 sm:hidden bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="grid grid-cols-5">
        {items.map((it) => {
          const active = it.match(pathname || '');
          const Icon = it.icon;
          if (it.klick) {
            return (
              <button key={it.href} type="button" onClick={it.klick}
                className="relative flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-gray-500">
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium leading-tight text-gray-600">{it.label}</span>
              </button>
            );
          }
          return (
            <Link
              key={it.href}
              href={it.href}
              className={`relative flex flex-col items-center justify-center gap-0.5 py-2 px-1 ${
                active ? 'text-brand' : 'text-gray-500'
              }`}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {it.badge && it.badge > 0 ? (
                  <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold">
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                ) : null}
              </div>
              <span className={`text-[10px] font-medium leading-tight ${active ? 'text-brand' : 'text-gray-600'}`}>
                {it.label}
              </span>
              {active && (
                <span className="absolute top-0 inset-x-0 h-0.5 bg-brand" aria-hidden />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
    </>
  );
}
